/**
 * RotationSwapRow — ONE change as a readable swap: [OUT ✗] ⟶ [IN ⊕].
 * Out cell red (requeue = blue Clock: back in line, not retired), in cell
 * green, missing side = dashed placeholder. Blocked renders as a full-width
 * warning cell.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { Clock, Hourglass, MoveRight, PlusCircle, Trash2 } from 'lucide-react';
import type { RotationElementDiff } from '@trafficpulse/shared';

import { Badge } from '@/components/ui';
import { cn } from '@/lib/utils';

import { humanizeDuration, testedForLabel } from '../queue-status.util';
import type { RotationSwap } from './rotation-swaps';

/** Tracker id chip — identity proof next to the name (bank-only ids hidden). */
export const IdChip: FC<{ itemId: string }> = ({ itemId }) =>
  itemId.startsWith('bank:') ? null : (
    <span className="shrink-0 font-mono text-[9px] text-muted-foreground">#{itemId}</span>
  );

function testedFor(diff: RotationElementDiff): string {
  const byDuration = humanizeDuration(diff.testDurationSeconds ?? null);
  return byDuration !== '—' ? byDuration : testedForLabel(diff.testStartedAt ?? null);
}

const OutMeta: FC<{ diff: RotationElementDiff }> = ({ diff }) => (
  <span className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
    {diff.reason ? <span>{diff.reason}</span> : null}
    {diff.removedByRuleId ? (
      <span className="rounded bg-muted px-1 py-0.5 text-[9px] font-medium">
        {diff.removedByRuleId}
      </span>
    ) : null}
    {/* Restore runs carry no test window — don't render "tested —" noise. */}
    {testedFor(diff) !== '—' ? <span>tested {testedFor(diff)}</span> : null}
    {diff.metricsSource === 'simulated' ? (
      <Badge tone="warning" className="text-[9px]">
        Simulated
      </Badge>
    ) : null}
  </span>
);

const OutCell: FC<{ diff?: RotationElementDiff }> = ({ diff }) => {
  if (!diff) {
    return (
      <div className="flex-1 rounded-md border border-dashed border-border/70 px-2.5 py-1.5 text-[10.5px] italic text-muted-foreground">
        fills a free slot
      </div>
    );
  }
  const requeue = diff.action === 'requeue';
  // A capacity eviction PARKS in Waiting — same non-destructive family as a
  // requeue, but it must not read as "back in line".
  const toWaiting = requeue && Boolean(diff.toWaiting);
  const Icon = toWaiting ? Hourglass : requeue ? Clock : Trash2;
  return (
    <div
      className={cn(
        'flex-1 rounded-md border px-2.5 py-1.5',
        requeue
          ? 'border-primary/40 bg-primary/10'
          : 'border-destructive/40 bg-destructive/10',
      )}
    >
      <p className="flex items-center gap-1.5 text-xs font-semibold">
        <Icon
          className={cn('h-3.5 w-3.5 shrink-0', requeue ? 'text-primary' : 'text-destructive')}
          aria-label={
            toWaiting
              ? 'Moved to waiting'
              : requeue
                ? 'Moved back to the queue'
                : 'Eliminated from the flow'
          }
        />
        <span className="truncate" title={diff.itemName}>
          {diff.itemName}
        </span>
        <IdChip itemId={diff.itemId} />
      </p>
      <OutMeta diff={diff} />
    </div>
  );
};

const InCell: FC<{ diff?: RotationElementDiff }> = ({ diff }) => {
  if (!diff) {
    return (
      <div className="flex-1 rounded-md border border-dashed border-border/70 px-2.5 py-1.5 text-[10.5px] italic text-muted-foreground">
        slot freed — nothing queued to fill it
      </div>
    );
  }
  return (
    <div className="flex-1 rounded-md border border-success/40 bg-success/10 px-2.5 py-1.5">
      <p className="flex items-center gap-1.5 text-xs font-semibold">
        <PlusCircle className="h-3.5 w-3.5 shrink-0 text-success" aria-label="Added from the queue" />
        <span className="truncate" title={diff.itemName}>
          {diff.itemName}
        </span>
        <IdChip itemId={diff.itemId} />
      </p>
      {diff.priority != null ? (
        <span className="text-[10px] tabular-nums text-muted-foreground">
          priority {diff.priority}
        </span>
      ) : null}
    </div>
  );
};

export const RotationSwapRow: FC<{ swap: RotationSwap }> = ({ swap }) => (
  <div className="flex items-stretch gap-2">
    <OutCell diff={swap.out} />
    <MoveRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground" aria-label="replaced by" />
    <InCell diff={swap.in} />
  </div>
);

// The blocked row lives in `./blocked` (this file was over the size limit).
